import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { ROW_HIGHLIGHT_ANIMATION } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/table';
import { getSecuritiesTableColumns } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/securitiesTableColumns';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { convertNumber } from '@dealstack/shared/lib/format/number';
import { formatAsOfFilterDate } from '@dealstack/frontend/shared/utils/utc.ts';
import { Button } from '@mantine/core';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
	CurrencyType,
	DataSecurityTct,
	GetPersonSecurityDataRes,
	PermissionRole,
	PaginationDto,
	SecurityCountsDto,
	SecurityFilterDto,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import {
	TableSorting,
	useMantineTableWithPaginationFilteringSorting,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	bulkCreateSecurities,
	getPersonSecurities,
	isSecuritiesUsedInAnyMip,
	softDeleteSecurity,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import { initialSorting } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/utils.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { ResourceType } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';

interface OneSchemaBulkUploadSecurity {
	currency: CurrencyType;
	name: string;
	type: SecurityType;
	nominalValue?: string;
	votesPerUnit?: string;
	description?: string;
}

interface GetSecuritiesParams {
	pagination?: PaginationDto;
	filter?: SecurityFilterDto;
	search?: string;
	sortBy?: TableSorting[];
}

enum PARAM_KEYS {
	FILTER_DATE = 'filterDate',
}

export const useSecuritiesController = (withHoldingTotals = false) => {
	const tCapManage = useTranslationCapManage();
	const capManageFlowController = useContext(CapManageControllerContext);
	const { selectedEntityData, selectedEntityName, selectedEntityId, refetchSelectedEntity } =
		capManageFlowController;

	const { dealId } = useContext(DealContext) || {};

	const [searchParams, setSearchParams] = useSearchParams();
	const prevEntityId = useRef(selectedEntityId);

	// Filter date state with URL persistence
	const urlFilterDate = searchParams.get(PARAM_KEYS.FILTER_DATE);
	const initialFilterDate = urlFilterDate ? new Date(urlFilterDate) : null;
	const [filterDate, setFilterDateInternal] = useState<Date | null>(initialFilterDate);

	const setFilterDate = useCallback(
		(newDate: Date | null) => {
			setFilterDateInternal(newDate);

			const newParams = new URLSearchParams(searchParams);
			if (newDate) {
				newParams.set(PARAM_KEYS.FILTER_DATE, newDate.toISOString());
			} else {
				newParams.delete(PARAM_KEYS.FILTER_DATE);
			}
			setSearchParams(newParams, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	// Reset filter date when entity changes
	useEffect(() => {
		if (prevEntityId.current && prevEntityId.current !== selectedEntityId) {
			setFilterDateInternal(null);

			const newParams = new URLSearchParams(searchParams);
			newParams.delete(PARAM_KEYS.FILTER_DATE);
			setSearchParams(newParams, { replace: true });
		}
		prevEntityId.current = selectedEntityId;
	}, [selectedEntityId, searchParams, setSearchParams]);

	const { hasPermission: hasEditorPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.EDITOR,
	});

	const { hasPermission: hasExportViewerPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.EXPORT_VIEWER,
	});

	const { hasPermission: userIsAdmin } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.ADMIN,
	});

	const oneSchemaConfig = useAppSelector((state) => state.user.data?.oneSchemaConfig);
	const bulkUploadSecuritiesRequest = useMutation({
		mutationFn: (variables: Parameters<typeof bulkCreateSecurities>[0]) =>
			bulkCreateSecurities(variables),
	});
	const deleteSecurityRequest = useMutation<
		Awaited<ReturnType<typeof softDeleteSecurity>>,
		{ error?: { code?: number; message?: string } },
		Parameters<typeof softDeleteSecurity>[0]
	>({
		mutationFn: softDeleteSecurity,
	});
	const isThereTsaInSecurity = useMutation({
		mutationFn: isSecuritiesUsedInAnyMip,
	});

	const [showOneSchemaModal, setShowOneSchemaModal] = useState(false);
	const [showUpdateCurrencyDrawer, setShowUpdateCurrencyDrawer] = useState(false);
	const [showCreateNewSecurityDrawer, setShowCreateNewSecurityDrawer] = useState(false);
	const [showEditSecurityDrawer, setShowEditSecurityDrawer] = useState(false);
	const [securityToEdit, setSecurityToEdit] = useState<DataSecurityTct>();
	const [isTsaExist, setIsTsaExist] = useState<boolean>(false);
	const [securityCounts, setSecurityCounts] = useState<SecurityCountsDto | undefined>(undefined);

	const columns = useMemo(
		() =>
			getSecuritiesTableColumns(
				tCapManage,
				setShowEditSecurityDrawer,
				setSecurityToEdit,
				hasEditorPermission,
				securityCounts,
				filterDate
			),
		[securityCounts, hasEditorPermission, filterDate]
	);

	const initialTableOptions: UseMantineTableDataOperationsPartialProps = {
		initialSorting,
	};
	const useStateFromUrlParams = true;
	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting<GetPersonSecurityDataRes, SecurityFilterDto>(
			columns,
			initialTableOptions,
			useStateFromUrlParams
		);

	const dataOperationsRef = useRef(dataOperations);
	const lastSelectedEntityIdRef = useRef<string | null>(null);
	const lastDataOperationsRef = useRef<typeof dataOperations>();

	useEffect(() => {
		dataOperationsRef.current = dataOperations;
	}, [dataOperations]);

	const resetPagination = useCallback(() => {
		mantineTableDataOperations.setPagination({
			pageIndex: 0,
			pageSize: dataOperations.pagination?.take ?? 50,
		});
	}, [mantineTableDataOperations, dataOperations.pagination?.take]);

	useEffect(() => {
		const prevEntity = lastSelectedEntityIdRef.current;
		const prevOps = lastDataOperationsRef.current;

		const entityChanged = prevEntity && prevEntity !== selectedEntityId;

		const searchChanged = prevOps && dataOperations.search !== prevOps.search;
		const filterChanged =
			prevOps && JSON.stringify(dataOperations.filter) !== JSON.stringify(prevOps.filter);

		if (entityChanged) {
			mantineTableDataOperations.resetAll();
			resetPagination();
		} else if ((searchChanged || filterChanged) && dataOperations.pagination?.skip !== 0) {
			resetPagination();
		}

		lastSelectedEntityIdRef.current = selectedEntityId;
		lastDataOperationsRef.current = dataOperations;
	}, [dataOperations, selectedEntityId, resetPagination, mantineTableDataOperations]);

	const [securitiesRequestParams, setSecuritiesRequestParams] =
		useState<GetSecuritiesParams | null>({
			pagination: dataOperations.pagination,
			filter: dataOperations.filter,
			search: dataOperations.search,
			sortBy: dataOperations.sortBy,
		});

	const getSecuritiesRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
				personId: selectedEntityId || '',
			}),
			{
				body: securitiesRequestParams,
				withHoldingTotals,
				filterDate: formatAsOfFilterDate(filterDate),
			},
		],

		queryFn: ({ signal }) =>
			getPersonSecurities(
				formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
					personId: selectedEntityId || '',
				}),
				{
					body: securitiesRequestParams ?? undefined,
					withHoldingTotals,
					filterDate: formatAsOfFilterDate(filterDate),
				},
				signal
			),
		placeholderData: keepPreviousData,
		enabled: !!selectedEntityId,
	});

	useEffect(() => {
		if (!getSecuritiesRequest.isSuccess) {
			return;
		}
		const data = getSecuritiesRequest.data;
		if (data) {
			const securities = data?.securities;

			if (securities && securities.length > 0) {
				setSecurityCounts(data.securityCounts);
			} else {
				setSecurityCounts(undefined);
			}
		}
	}, [getSecuritiesRequest.isSuccess, getSecuritiesRequest.data]);

	useEffect(() => {
		setSecuritiesRequestParams({
			pagination: dataOperations.pagination,
			filter: dataOperations.filter,
			search: dataOperations.search,
			sortBy: dataOperations.sortBy,
		});
	}, [dataOperations]);

	const securitiesRefetch = useCallback(
		(skipRefetch = false) => {
			if (!skipRefetch) {
				void getSecuritiesRequest.refetch();
			}
		},
		[getSecuritiesRequest]
	);

	const onBulkUploadSecurities = ({ records }: { records: OneSchemaBulkUploadSecurity[] }) => {
		if (!selectedEntityData) {
			return;
		}

		bulkUploadSecuritiesRequest.mutate(
			{
				issuerId: selectedEntityId,
				securities: records.map((security) => ({
					name: security.name,
					type: security.type,
					currency: security.currency,
					nominalValue: convertNumber(security.nominalValue),
					votesPerUnit: convertNumber(security.votesPerUnit),
				})),
			},
			{
				onSuccess: (resp) => {
					securitiesRefetch();
					void refetchSelectedEntity();
					showToastV2({
						message: `${resp?.count} ${tCapManage('toasts.success.security_uploaded_successfully')}`,
					});
				},
			}
		);
	};

	const onDelete = useCallback((securityId: string) => {
		deleteSecurityRequest.mutate(securityId, {
			onSuccess: () => {
				securitiesRefetch();
				void refetchSelectedEntity();
				showToastV2({
					message: tCapManage('toasts.success.security_deleted_successfully'),
				});
			},
		});
	}, []);

	const fetchTsaData = async (ids: string[]) => {
		const data = await isThereTsaInSecurity.mutateAsync(ids);
		setIsTsaExist(!!data?.isSecuritiesUsedInAnyMip);
	};

	const tableRef = useRef<HTMLDivElement | null>(null);

	const onCreateSecuritiesModalClose = async (createdSecurityId?: string | Event) => {
		setShowCreateNewSecurityDrawer(false);
		if (!createdSecurityId) {
			return;
		}
		if (typeof createdSecurityId !== 'string') {
			return;
		}

		securitiesRefetch();
		void refetchSelectedEntity();
		const updatedSecurities = getSecuritiesRequest.data;

		const isCreatedSecurityExistOnCurrentPage = (updatedSecurities?.securities ?? []).some(
			(row) => row.id === createdSecurityId
		);

		//we have to highlight created security if its on current page - D.Zghinnik 11.10.2023
		if (isCreatedSecurityExistOnCurrentPage) {
			//set timeout with 0 needed to run query selector after dom re rendered - D.Zghinnik 11.10.2023
			setTimeout(() => {
				const row = tableRef?.current?.querySelector(`tr[id="${createdSecurityId}"]`);

				row?.scrollIntoView({ block: 'start', behavior: 'smooth' });
				row?.animate(ROW_HIGHLIGHT_ANIMATION.steps, ROW_HIGHLIGHT_ANIMATION.options);
			}, 0);
			return;
		}

		showToastV2({
			title: tCapManage('toasts.success.security_created_successfully'),
			message: (
				<Button
					onClick={() => {
						mantineTableDataOperations.setGlobalSearchString(createdSecurityId);
						mantineTableDataOperations.setShowGlobalSearchStringInput(true);
					}}
				>
					{tCapManage('toasts.buttons.jump_to_record')}
				</Button>
			),
		});
	};

	const showTable =
		mantineTableDataOperations.isFiltersApplied ||
		!!getSecuritiesRequest.data?.securities?.length ||
		getSecuritiesRequest.isLoading ||
		getSecuritiesRequest.isFetching;

	return {
		dataOperations,
		dataOperationsRef,
		selectedEntityId,
		tableRef,
		selectedEntityName,
		onBulkUploadSecurities,
		oneSchemaConfig,
		getSecuritiesRequest,
		mantineTableDataOperations,
		deleteSecurityRequest,
		columns,
		onDelete,
		showOneSchemaModal,
		setShowOneSchemaModal,
		showCreateNewSecurityDrawer,
		setShowCreateNewSecurityDrawer,
		showUpdateCurrencyDrawer,
		setShowUpdateCurrencyDrawer,
		showEditSecurityDrawer,
		setShowEditSecurityDrawer,
		securityToEdit,
		setSecurityToEdit,
		onCreateSecuritiesModalClose,
		securitiesRefetch,
		showTable,
		isTableLoading: getSecuritiesRequest.isFetching || deleteSecurityRequest.isPending,
		isLoading: getSecuritiesRequest.isLoading || bulkUploadSecuritiesRequest.isPending,
		isTsaExist,
		setIsTsaExist,
		fetchTsaData,
		userIsAdmin,
		refetchSelectedEntity,
		selectedEntityData,
		dealId,
		hasEditorPermission,
		hasExportViewerPermission,
		filterDate,
		setFilterDate,
	};
};
